import { CACHE_TIME } from '@/api/client';
import type { QueryCriteria, ResponseType } from '@/api/types';
import { queryOptions } from '@tanstack/react-query';

import type { CrudEndpoints } from '../endpoints/createCrudEndpoints';

export function createQueryFactory<TCore, TFlat, TEx, TInfo>(
  entityKey: string,
  endpoints: CrudEndpoints<TCore, TFlat, TEx, TInfo>
) {
  return {
    all: () => [entityKey] as const,

    lists: () => [entityKey, 'list'] as const,

    listCore: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'core'] as const,
        queryFn: () => endpoints.getAllCore(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    listFlat: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'flat'] as const,
        queryFn: () => endpoints.getAllFlat(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    listHierarchy: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'hierarchy'] as const,
        queryFn: () => endpoints.getAllHierarchy(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    listInfo: (criteria?: QueryCriteria) =>
      queryOptions({
        queryKey: [entityKey, 'list', criteria, 'info'] as const,
        queryFn: () => endpoints.getAllInfo(criteria),
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    details: () => [entityKey, 'detail'] as const,

    detail: (id: string, resType: ResponseType = 'hierarchy') =>
      queryOptions({
        queryKey: [entityKey, 'detail', id, resType] as const,
        queryFn: () => endpoints.getById(id, resType),
        enabled: !!id,
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    detailCore: (id: string) =>
      queryOptions({
        queryKey: [entityKey, 'detail', id, 'core'] as const,
        queryFn: () => endpoints.getCoreById(id),
        enabled: !!id,
        staleTime: CACHE_TIME.STALE,
        gcTime: CACHE_TIME.GC
      }),

    // No-op queries for Suspense hooks when id is undefined (e.g., "new" mode)
    detailNoop: () => ({
      queryKey: [entityKey, 'detail', 'noop'] as const,
      queryFn: () => Promise.resolve(null),
      staleTime: Infinity
    }),

    detailCoreNoop: () => ({
      queryKey: [entityKey, 'detailCore', 'noop'] as const,
      queryFn: () => Promise.resolve(null),
      staleTime: Infinity
    })
  };
}

// Export type helper for consumers
export type QueryFactory<TCore, TFlat, TEx, TInfo> = ReturnType<typeof createQueryFactory<TCore, TFlat, TEx, TInfo>>;

/**
 * Wraps a caller-supplied `queryFn` in the same `queryOptions` shape the CRUD factory produces, so
 * custom-URL non-CRUD queries (e.g. `/entity/{id}/descendant-counts`, `/entity/{id}/dependencies`)
 * route through one construction path with the CRUD queries. Applies the module-standard
 * `CACHE_TIME.STALE` / `CACHE_TIME.GC` unless the caller opts out, and forwards `enabled` so a hook
 * can gate the fetch on caller state.
 *
 * Callers should embed the returned options in a namespaced key derived from their entity's
 * `factory.all()` so invalidation of the CRUD roots also invalidates the custom queries:
 *
 * ```
 * useQuery(createCustomQuery(
 *   [...itemCategoriesQueries.all(), id, 'descendant-counts'],
 *   () => endpoints.getDescendantCounts(id),
 *   { enabled: Boolean(id) }
 * ))
 * ```
 *
 * Documented in api-patterns.md as the sanctioned extension point for endpoints that can't be
 * expressed as CRUD.
 */
export function createCustomQuery<TData>(
  queryKey: readonly unknown[],
  queryFn: () => Promise<TData>,
  options?: { enabled?: boolean; staleTime?: number; gcTime?: number }
) {
  return queryOptions({
    queryKey,
    queryFn,
    staleTime: options?.staleTime ?? CACHE_TIME.STALE,
    gcTime: options?.gcTime ?? CACHE_TIME.GC,
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {})
  });
}
